import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  AutoComplete,
  Button,
  DatePicker,
  Modal,
  Table,
  message,
  Input,
  Tooltip,
  Upload,
} from 'antd'
import {
  PlusOutlined,
  ThunderboltOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import TableColumn from './TableColumn'
import styled from 'styled-components/macro'
import { useSelector, useDispatch } from 'react-redux'
import {
  fetchSupplierProducts,
  resetProducts,
} from 'src/features/ManualOrderProductsSlice'
import {
  sumManualOrderExVatTotal,
  sumManualOrderGrossTotal,
  sumManualOrderVatTotal,
} from 'src/utils/manualOrderVatHelpers'
import {
  proposeManualOrderFromUpload,
  resolveManualOrderProposalProducts,
} from 'src/utils/manualOrderProposalApi'
import {
  mapProposalToManualOrderDraft,
  mapResolvedProductsToOrderRows,
  shouldLockManualOrderDates,
} from 'src/utils/applyManualOrderProposal'

function normaliseSupplierCompareKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
}

function SupplierConfirmBody({ printedName, suggestedTitle }) {
  const sameName =
    normaliseSupplierCompareKey(printedName) ===
    normaliseSupplierCompareKey(suggestedTitle)

  if (sameName) {
    return (
      <SupplierConfirmWrap>
        <SupplierConfirmLead>
          This invoice matches a supplier already on your venue.
        </SupplierConfirmLead>
        <SupplierConfirmCard>
          <SupplierConfirmLabel>Venue supplier</SupplierConfirmLabel>
          <SupplierConfirmValue title={suggestedTitle}>
            {suggestedTitle}
          </SupplierConfirmValue>
        </SupplierConfirmCard>
        <SupplierConfirmHint>
          Use this supplier for the manual order, or choose a different one.
        </SupplierConfirmHint>
      </SupplierConfirmWrap>
    )
  }

  return (
    <SupplierConfirmWrap>
      <SupplierConfirmLead>
        We found an existing venue supplier that looks like a match.
      </SupplierConfirmLead>
      <SupplierConfirmRows>
        <SupplierConfirmCard>
          <SupplierConfirmLabel>On invoice</SupplierConfirmLabel>
          <SupplierConfirmValue title={printedName || '—'}>
            {printedName || '—'}
          </SupplierConfirmValue>
        </SupplierConfirmCard>
        <SupplierConfirmCard accent>
          <SupplierConfirmLabel>Venue supplier</SupplierConfirmLabel>
          <SupplierConfirmValue title={suggestedTitle}>
            {suggestedTitle}
          </SupplierConfirmValue>
        </SupplierConfirmCard>
      </SupplierConfirmRows>
      <SupplierConfirmHint>
        Use the venue supplier for this order, or pick / create a different one.
      </SupplierConfirmHint>
    </SupplierConfirmWrap>
  )
}

function askCreateManualSupplier(printedName, resolve) {
  Modal.confirm({
    title: 'Create new manual supplier?',
    width: 480,
    okText: 'Create supplier',
    cancelText: 'Pick myself',
    className: 'manual-order-supplier-confirm',
    okButtonProps: {
      style: { background: '#7cb305', borderColor: '#7cb305' },
    },
    content: (
      <SupplierConfirmWrap>
        <SupplierConfirmLead>
          No other venue supplier was chosen. Create a new manual supplier from
          the invoice name?
        </SupplierConfirmLead>
        <SupplierConfirmCard>
          <SupplierConfirmLabel>New supplier name</SupplierConfirmLabel>
          <SupplierConfirmValue title={printedName}>
            {printedName}
          </SupplierConfirmValue>
        </SupplierConfirmCard>
        <SupplierConfirmHint>
          This stays manual / historical — nothing is sent to the supplier.
        </SupplierConfirmHint>
      </SupplierConfirmWrap>
    ),
    onOk: () => {
      resolve({ type: 'create', title: printedName })
    },
    onCancel: () => {
      resolve({ type: 'none' })
    },
  })
}

function askSupplierChoiceForProposal(proposal = {}) {
  const printedName = String(
    proposal?.supplierMatch?.printedName ||
      proposal?.createSupplierTitle ||
      proposal?.supplierName ||
      '',
  ).trim()
  const suggested = proposal?.supplierMatch?.suggested || null
  const suggestedTitle = String(suggested?.supplierTitle || '').trim()
  const suggestedId = String(suggested?.supplierId || '').trim()

  return new Promise(resolve => {
    if (suggested && suggestedId && suggestedTitle) {
      Modal.confirm({
        title: 'Confirm supplier',
        width: 520,
        okText: 'Use this supplier',
        cancelText: 'Different supplier',
        className: 'manual-order-supplier-confirm',
        okButtonProps: {
          style: { background: '#7cb305', borderColor: '#7cb305' },
        },
        content: (
          <SupplierConfirmBody
            printedName={printedName}
            suggestedTitle={suggestedTitle}
          />
        ),
        onOk: () => {
          resolve({ type: 'existing', supplierId: suggestedId })
        },
        onCancel: () => {
          if (!printedName) {
            resolve({ type: 'none' })
            return
          }
          askCreateManualSupplier(printedName, resolve)
        },
      })
      return
    }

    if (printedName) {
      askCreateManualSupplier(printedName, resolve)
      return
    }

    resolve({ type: 'none' })
  })
}

const { Option } = AutoComplete
const { TextArea } = Input
const isObjectId = value => /^[a-f\d]{24}$/i.test(String(value || '').trim())

const AddInvoiceModal = ({
  showModal,
  order,
  setOrder,
  suppliers,
  initialPoNumber = '',
  /** Optional AI proposal to apply when the modal opens (Invoices flow). */
  proposalToApply = null,
  onProposalApplied = null,
  /** Orders flow only — hide on Create order from invoice (already have the file). */
  showAiBuildPanel = true,
  /** Notify parent of whole-order discount pounds so it can persist after create. */
  onWholeOrderDiscountChange = null,
}) => {
  const dispatch = useDispatch()
  const [supplierId, setSupplierId] = useState('')
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('')
  const [filteredSupplier, setFilteredSupplier] = useState(
    suppliers.filter(
      supplier =>
        supplier.supplier != null &&
        (supplier.isActive === true || supplier.manualOrdersOnly === true),
    ),
  )
  const [selectedProducts, setSelectedProducts] = useState([])
  const [orderedDate, setOrderedDate] = useState(null)
  const [deliveryDate, setDeliveryDate] = useState(null)
  const [canAddProduct, setCanAddProduct] = useState(false)
  const [selectedSupplier, setSelectedSupplier] = useState(null)
  const [poNumber, setPONumber] = useState('')
  const [isManualSupplierSelected, setIsManualSupplierSelected] =
    useState(false)
  const [supplierSageContactNumber, setSupplierSageContactNumber] = useState('')
  const [aiNotes, setAiNotes] = useState('')
  const [aiFile, setAiFile] = useState(null)
  const [aiLoading, setAiLoading] = useState(false)
  const [aiProposalApplied, setAiProposalApplied] = useState(false)
  const [aiWarnings, setAiWarnings] = useState([])
  /** Whole-order discount from AI invoice extraction (pounds). */
  const [aiOrderDiscount, setAiOrderDiscount] = useState(null)
  const [aiResolvingProducts, setAiResolvingProducts] = useState(false)
  const appliedProposalKeyRef = useRef('')
  /** Raw AI line guesses held until supplier is confirmed / picked. */
  const pendingAiLineItemsRef = useRef({
    lineItems: [],
    lineItemsIncludeVat: null,
  })

  // Get paginated products from Redux store
  const {
    products: supplierProducts,
    isLoading: productsLoading,
    hasMore,
    currentPage,
  } = useSelector(
    state =>
      state.manualOrderProducts || {
        products: [],
        isLoading: false,
        hasMore: false,
        currentPage: 1,
      },
  )

  const venue = useSelector(state => state.venue.venue)
  const usageLimits =
    useSelector(state => state.venue?.venue?.usageLimits) ?? {}
  const currentPlan = usageLimits?.currentPlan
  const features = usageLimits?.features
  const { PO_Numbers } = features || {}

  const handleSearch = value => {
    setSupplierSearchTerm(value)
    setFilteredSupplier(
      suppliers.filter(
        supplier =>
          supplier.supplier != null &&
          (supplier.isActive === true || supplier.manualOrdersOnly === true) &&
          supplier.supplierTitle.toLowerCase().includes(value.toLowerCase()),
      ),
    )
  }

  useEffect(() => {
    setFilteredSupplier(
      suppliers.filter(
        supplier =>
          supplier.supplier != null &&
          (supplier.isActive === true || supplier.manualOrdersOnly === true),
      ),
    )
  }, [showModal, suppliers])

  useEffect(() => {
    if (!showModal) {
      setSupplierId('')
      setSupplierSearchTerm('')
      setSelectedProducts([])
      setOrderedDate(null)
      setDeliveryDate(null)
      setSelectedSupplier(null)
      setIsManualSupplierSelected(false)
      setSupplierSageContactNumber('')
      setPONumber('')
      setAiNotes('')
      setAiFile(null)
      setAiLoading(false)
      setAiProposalApplied(false)
      setAiWarnings([])
      setAiOrderDiscount(null)
      setAiResolvingProducts(false)
      appliedProposalKeyRef.current = ''
      pendingAiLineItemsRef.current = {
        lineItems: [],
        lineItemsIncludeVat: null,
      }
      dispatch(resetProducts())
      return
    }

    const poPrefill = String(initialPoNumber || '').trim()
    if (poPrefill) {
      setPONumber(poPrefill)
      setOrder(prev => ({ ...prev, PONumber: poPrefill }))
    }
  }, [showModal, dispatch, initialPoNumber, setOrder])

  const resolveAndApplyProducts = async ({
    supplierIdForMatch,
    orderedDateValue,
    deliveryDateValue,
  }) => {
    const pending = pendingAiLineItemsRef.current || {}
    const lineItems = Array.isArray(pending.lineItems) ? pending.lineItems : []
    if (!venue?._id || !lineItems.length) {
      setSelectedProducts([])
      return []
    }

    setAiResolvingProducts(true)
    const hide = message.loading('Matching products for this supplier…', 0)
    try {
      const result = await resolveManualOrderProposalProducts({
        venueId: venue._id,
        supplierId: isObjectId(supplierIdForMatch) ? supplierIdForMatch : null,
        lineItems,
        lineItemsIncludeVat: pending.lineItemsIncludeVat,
      })
      const rows = mapResolvedProductsToOrderRows(result?.products || [], {
        orderedDate: orderedDateValue,
        deliveryDate: deliveryDateValue,
      })
      setSelectedProducts(rows)
      setOrder(prev => ({ ...prev, products: rows }))
      pendingAiLineItemsRef.current = {
        lineItems: [],
        lineItemsIncludeVat: null,
      }
      return rows
    } finally {
      hide()
      setAiResolvingProducts(false)
    }
  }

  const applyProposalDraft = async (proposal, { warnings = [] } = {}) => {
    // Step 1: confirm supplier only (AI has not matched catalogue products yet).
    const supplierChoice = await askSupplierChoiceForProposal(proposal)
    pendingAiLineItemsRef.current = {
      lineItems: Array.isArray(proposal?.lineItems) ? proposal.lineItems : [],
      lineItemsIncludeVat: proposal?.lineItemsIncludeVat ?? null,
    }

    const draft = mapProposalToManualOrderDraft(proposal, {
      suppliers,
      supplierChoice,
    })
    setSupplierId(draft.supplierId)
    setSupplierSearchTerm(draft.supplierSearchTerm)
    setSelectedSupplier(draft.selectedSupplier)
    setIsManualSupplierSelected(draft.isManualSupplierSelected)
    setSupplierSageContactNumber(draft.supplierSageContactNumber)
    setOrderedDate(draft.orderedDate)
    setDeliveryDate(draft.deliveryDate)
    if (draft.poNumber) setPONumber(draft.poNumber)
    setSelectedProducts([])
    setAiProposalApplied(true)
    setAiWarnings(Array.isArray(warnings) ? warnings : [])
    const discountAmount = Number(proposal?.orderDiscount?.amount)
    setAiOrderDiscount(
      Number.isFinite(discountAmount) && discountAmount > 0
        ? {
            amount: discountAmount,
            type: proposal?.orderDiscount?.type || 'fixed',
          }
        : null,
    )
    setOrder(prev => ({
      ...prev,
      ...draft.orderPatch,
      products: [],
      PONumber: draft.poNumber || prev?.PONumber || '',
    }))

    if (venue?._id && isObjectId(draft.supplierId)) {
      await dispatch(
        fetchSupplierProducts({
          venueId: venue._id,
          supplierId: draft.supplierId,
          page: 1,
          pageSize: 200,
          isManualSupplier: draft.isManualSupplierSelected,
        }),
      )
    } else {
      dispatch(resetProducts())
    }

    // Step 2: only after supplier is known — match catalogue or keep AI guesses.
    if (
      supplierChoice?.type === 'existing' ||
      supplierChoice?.type === 'create'
    ) {
      try {
        await resolveAndApplyProducts({
          supplierIdForMatch:
            supplierChoice.type === 'existing' ? draft.supplierId : null,
          orderedDateValue: draft.orderedDate,
          deliveryDateValue: draft.deliveryDate,
        })
        message.success(
          supplierChoice.type === 'existing'
            ? `Using ${
                draft.supplierSearchTerm || 'venue supplier'
              } — products matched from catalogue where possible`
            : 'Manual supplier set — showing Ollie-guessed products to review',
        )
      } catch (error) {
        message.error(error?.message || 'Could not match products')
      }
    } else {
      message.info(
        'Choose a supplier next — products will be matched after you pick one',
      )
    }
    onProposalApplied?.(proposal)
  }

  useEffect(() => {
    if (!showModal || !proposalToApply?.proposal) return undefined
    const key = JSON.stringify({
      supplier: proposalToApply.proposal.supplierName,
      po: proposalToApply.proposal.poNumber,
      lines: proposalToApply.proposal.products?.length,
      at: proposalToApply.appliedAt || proposalToApply.proposal.confidence,
    })
    if (appliedProposalKeyRef.current === key) return undefined
    appliedProposalKeyRef.current = key
    applyProposalDraft(proposalToApply.proposal, {
      warnings: proposalToApply.warnings,
    })
    return undefined
  }, [showModal, proposalToApply])

  const handleBuildWithAi = async () => {
    if (!venue?._id) {
      message.error('Venue not loaded yet')
      return
    }
    if (!aiFile && !String(aiNotes || '').trim()) {
      message.warning('Upload an invoice/receipt or add a short note first')
      return
    }

    setAiLoading(true)
    try {
      const result = await proposeManualOrderFromUpload({
        venueId: venue._id,
        file: aiFile,
        notes: aiNotes,
      })
      if (!result?.proposal) {
        throw new Error('No proposed order returned')
      }
      await applyProposalDraft(result.proposal, { warnings: result.warnings })
    } catch (error) {
      message.error(error?.message || 'Could not build order with Ollie')
    } finally {
      setAiLoading(false)
    }
  }

  const handleAddProduct = () => {
    if (!supplierId) {
      message.warning('Please choose a supplier first')
      return
    }
    if (!orderedDate || !deliveryDate) {
      message.warning('Please set ordered and delivery dates first')
      return
    }

    const newProd = {
      key: new Date().getTime().toString(),
      orderedDate,
      deliveryDate,
      product: '',
      hasVat: false,
      priceIncludesVat: false,
    }
    setSelectedProducts([...selectedProducts, newProd])
  }

  const handleSelect = async value => {
    const pendingLines = pendingAiLineItemsRef.current?.lineItems || []
    const shouldResolveAiProducts =
      aiProposalApplied &&
      Array.isArray(pendingLines) &&
      pendingLines.length > 0

    if (value.startsWith('__create__:')) {
      const supplierTitle = value.replace('__create__:', '').trim()
      if (!supplierTitle) return
      setSupplierId(supplierTitle)
      setIsManualSupplierSelected(true)
      setSelectedSupplier({
        supplierTitle,
        supplier: { title: supplierTitle, deliveryDays: [] },
        manualOrdersOnly: true,
        isManualCreated: true,
      })
      setSupplierSearchTerm(supplierTitle)
      setSupplierSageContactNumber('')
      setOrder(prev => ({
        ...prev,
        supplier: supplierTitle,
        supplierSageContactNumber: '',
      }))
      dispatch(resetProducts())
      if (shouldResolveAiProducts) {
        try {
          await resolveAndApplyProducts({
            supplierIdForMatch: null,
            orderedDateValue: orderedDate,
            deliveryDateValue: deliveryDate,
          })
          message.success(
            'Manual supplier set — showing Ollie-guessed products to review',
          )
        } catch (error) {
          message.error(error?.message || 'Could not apply guessed products')
        }
      }
      return
    }

    const supplier = suppliers.find(s => s.supplier?._id === value)
    if (!supplier) {
      message.error('Could not select supplier. Please try again.')
      return
    }
    setSupplierId(value)
    const isExistingManualSupplier = Boolean(
      supplier?.isManualCreated || supplier?.nonOMSSupplier,
    )
    setIsManualSupplierSelected(isExistingManualSupplier)
    setSelectedSupplier(supplier)
    setSupplierSearchTerm(supplier.supplierTitle || '')
    const existingSageContactNumber = String(
      supplier?.sageSupplierAccountNumber || '',
    ).trim()
    setSupplierSageContactNumber(existingSageContactNumber)
    setOrder(prev => ({
      ...prev,
      supplier: value,
      supplierSageContactNumber: existingSageContactNumber,
    }))

    // Fetch first 200 products for the selected supplier
    if (venue?._id) {
      await dispatch(
        fetchSupplierProducts({
          venueId: venue._id,
          supplierId: value,
          page: 1,
          pageSize: 200,
          isManualSupplier: isExistingManualSupplier,
        }),
      )
    }

    if (shouldResolveAiProducts) {
      try {
        await resolveAndApplyProducts({
          supplierIdForMatch: value,
          orderedDateValue: orderedDate,
          deliveryDateValue: deliveryDate,
        })
        message.success(
          'Products matched from this supplier’s catalogue where possible',
        )
      } catch (error) {
        message.error(error?.message || 'Could not match products')
      }
    }
  }
  const selectOrderedDate = date => {
    setOrderedDate(date)
    setOrder({ ...order, orderedDate: date })
  }

  const selectDeliveryDate = date => {
    const day = date.format('ddd').toLowerCase()
    const isManualOnlySupplier = selectedSupplier?.manualOrdersOnly === true
    const deliveryDays = selectedSupplier?.supplier?.deliveryDays || []
    // Always set the delivery date, but show warning if invalid
    // Users may want emergency deliveries on non-standard days
    if (
      selectedSupplier &&
      !isManualOnlySupplier &&
      !deliveryDays.includes(day)
    ) {
      message.warning({
        content: `${selectedSupplier.supplier.title} doesn't typically deliver on ${day}. You can still place the order if needed.`,
        duration: 6,
      })
    }
    // Set the date regardless of validation
    setDeliveryDate(date)
    setOrder({ ...order, deliveryDate: date })
  }

  // After AI supplier confirm (or a pick once AI ran), supplier cannot be changed.
  const lockSupplier = aiProposalApplied && Boolean(supplierId)
  const lockDates = shouldLockManualOrderDates({
    aiProposalApplied,
    selectedProductCount: selectedProducts.length,
  })

  useEffect(() => {
    setOrder({ ...order, products: selectedProducts })
  }, [JSON.stringify(selectedProducts)])

  const invalidProductsCount = selectedProducts.filter(item => {
    const title = String(item?.title || '').trim()
    const hasProductId = isObjectId(item?.product)
    const quantity = Number(item?.quantity)
    const unitPrice = Number(item?.OMSPrice)
    return (
      (!hasProductId && !title) ||
      !Number.isFinite(quantity) ||
      quantity <= 0 ||
      !Number.isFinite(unitPrice) ||
      unitPrice < 0
    )
  }).length

  const orderGrossTotal = useMemo(
    () => sumManualOrderGrossTotal(selectedProducts),
    [selectedProducts],
  )
  const orderExVatTotal = useMemo(
    () => sumManualOrderExVatTotal(selectedProducts),
    [selectedProducts],
  )
  const orderVatTotal = useMemo(
    () => sumManualOrderVatTotal(selectedProducts),
    [selectedProducts],
  )
  const orderDiscountAmount = useMemo(() => {
    const amount = Number(aiOrderDiscount?.amount)
    return Number.isFinite(amount) && amount > 0 ? amount : 0
  }, [aiOrderDiscount])
  const orderPayableTotal = useMemo(() => {
    const payable = orderGrossTotal - orderDiscountAmount
    return Math.round(Math.max(0, payable) * 100) / 100
  }, [orderGrossTotal, orderDiscountAmount])

  useEffect(() => {
    if (typeof onWholeOrderDiscountChange !== 'function') return
    onWholeOrderDiscountChange(orderDiscountAmount)
  }, [orderDiscountAmount, onWholeOrderDiscountChange])

  useEffect(() => {
    const hasRequiredDates = Boolean(orderedDate && deliveryDate)
    if (!hasRequiredDates) {
      setCanAddProduct(false)
      return
    }

    // Allow adding rows as long as supplier and dates are set.
    // Product selection/creation validity is enforced at row/submit validation.
    setCanAddProduct(true)
  }, [orderedDate, deliveryDate, supplierId])

  // Remove the old useEffect that filtered products from the props
  // Products are now managed by Redux store

  return (
    <div>
      {showModal && showAiBuildPanel ? (
        <AiBuildPanel>
          <AiBuildHeader>
            <ThunderboltOutlined style={{ color: '#7cb305' }} />
            <div>
              <AiBuildTitle>Build order with Ollie</AiBuildTitle>
              <AiBuildHint>
                Upload an invoice/receipt and/or paste what you know. Ollie
                confirms the supplier first, then matches products — nothing is
                sent to the supplier.
              </AiBuildHint>
            </div>
          </AiBuildHeader>
          <AiBuildControls>
            <Upload
              accept='.pdf,image/jpeg,image/png,image/webp'
              beforeUpload={file => {
                setAiFile(file)
                return false
              }}
              onRemove={() => setAiFile(null)}
              fileList={
                aiFile
                  ? [
                      {
                        uid: 'ai-manual-order',
                        name: aiFile.name,
                        status: 'done',
                      },
                    ]
                  : []
              }
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>
                Upload invoice / receipt
              </Button>
            </Upload>
            <TextArea
              rows={2}
              value={aiNotes}
              onChange={event => setAiNotes(event.target.value)}
              placeholder='Optional notes (supplier, dates, products, totals…)'
              style={{ flex: 1, minWidth: 220 }}
            />
            <Button
              type='primary'
              loading={aiLoading || aiResolvingProducts}
              onClick={handleBuildWithAi}
              style={{ background: '#7cb305', borderColor: '#7cb305' }}
            >
              Build with Ollie
            </Button>
          </AiBuildControls>
          {aiProposalApplied ? (
            <Alert
              type='info'
              showIcon
              style={{ marginTop: 12 }}
              message={
                aiResolvingProducts
                  ? 'Matching products for this supplier…'
                  : 'Proposed order — review and correct anything before Add manual order'
              }
              description={
                aiWarnings.length
                  ? `Notes: ${aiWarnings.join(', ')}`
                  : lockSupplier
                  ? 'Supplier is locked. Dates and lines are editable below.'
                  : 'Choose a supplier to unlock product matching.'
              }
            />
          ) : null}
        </AiBuildPanel>
      ) : null}
      {showModal && !showAiBuildPanel && aiProposalApplied ? (
        <Alert
          type='info'
          showIcon
          style={{ marginBottom: 16 }}
          message={
            aiResolvingProducts
              ? 'Matching products for this supplier…'
              : 'Proposed order from this invoice — review before confirming'
          }
          description={
            aiWarnings.length
              ? `Notes: ${aiWarnings.join(', ')}`
              : lockSupplier
              ? 'Supplier is locked. Dates and lines are editable below.'
              : 'Choose a supplier to unlock product matching.'
          }
        />
      ) : null}
      <OrderHeader>
        {showModal && (
          <>
            <HeaderPrimary hasSage={isManualSupplierSelected}>
              {currentPlan === 'Free' && PO_Numbers.limit === 0 ? (
                <Tooltip
                  placement='topRight'
                  title={
                    'You have reached the limit of your current plan for this month.Please upgrade your subscription or email info@ordersmadesimple.com to find out more'
                  }
                >
                  <HeaderField>
                    <Input
                      disabled={
                        currentPlan === 'Free' && PO_Numbers.limit === 0
                      }
                      placeholder='PO Number'
                      value={poNumber}
                      onChange={event => {
                        setPONumber(event.target.value)
                      }}
                      onBlur={() => {
                        setOrder({ ...order, PONumber: poNumber?.trim() })
                      }}
                    />
                  </HeaderField>
                </Tooltip>
              ) : (
                <HeaderField>
                  <Input
                    placeholder='PO Number'
                    value={poNumber}
                    onChange={event => {
                      setPONumber(event.target.value)
                    }}
                    onBlur={() => {
                      setOrder({ ...order, PONumber: poNumber?.trim() })
                    }}
                  />
                </HeaderField>
              )}

              <HeaderField>
                <AutoComplete
                  value={supplierSearchTerm}
                  disabled={lockSupplier}
                  onSearch={handleSearch}
                  onSelect={handleSelect}
                  notFoundContent={
                    supplierSearchTerm?.trim()
                      ? `Create new supplier "${supplierSearchTerm.trim()}"`
                      : 'Supplier not found'
                  }
                  placeholder={'Choose your supplier'}
                  style={{ width: '100%' }}
                >
                  {filteredSupplier.map(supplier => (
                    <Option key={supplier._id} value={supplier.supplier._id}>
                      {supplier.supplierTitle} &nbsp; &nbsp;
                      {supplier?.supplier?.secondTitle}
                    </Option>
                  ))}
                  {supplierSearchTerm?.trim() && (
                    <Option
                      key={`create-${supplierSearchTerm.trim().toLowerCase()}`}
                      value={`__create__:${supplierSearchTerm.trim()}`}
                    >
                      <CreateSupplierOptionRow>
                        <CreateSupplierIcon>
                          <PlusOutlined />
                        </CreateSupplierIcon>
                        <CreateSupplierText>
                          Create new supplier:{' '}
                          <strong>
                            &quot;{supplierSearchTerm.trim()}&quot;
                          </strong>
                        </CreateSupplierText>
                      </CreateSupplierOptionRow>
                    </Option>
                  )}
                </AutoComplete>
              </HeaderField>
              {isManualSupplierSelected && (
                <HeaderField>
                  <Input
                    placeholder='Sage Contact Number'
                    value={supplierSageContactNumber}
                    onChange={event => {
                      const value = event.target.value
                      setSupplierSageContactNumber(value)
                      setOrder({
                        ...order,
                        supplierSageContactNumber: value,
                      })
                    }}
                  />
                </HeaderField>
              )}
            </HeaderPrimary>
            <HeaderDates>
              <HeaderField>
                <DatePicker
                  disabled={!supplierId || lockDates}
                  onChange={selectOrderedDate}
                  placeholder={'Ordered date'}
                  value={orderedDate}
                  style={{ width: '100%' }}
                />
              </HeaderField>
              <HeaderField>
                <DatePicker
                  disabled={!supplierId || lockDates}
                  onChange={selectDeliveryDate}
                  placeholder={'Delivery date'}
                  value={deliveryDate}
                  style={{ width: '100%' }}
                />
              </HeaderField>
            </HeaderDates>
          </>
        )}
      </OrderHeader>
      <TableToolbar>
        <ToolbarHint>
          {invalidProductsCount > 0 ? (
            <ValidationHint>
              {invalidProductsCount} line
              {invalidProductsCount > 1 ? 's are' : ' is'} incomplete (needs
              product title or selection, quantity & price).
            </ValidationHint>
          ) : (
            <ToolbarHintText>Review product lines below</ToolbarHintText>
          )}
        </ToolbarHint>
        <Button
          disabled={!canAddProduct}
          type={'primary'}
          onClick={handleAddProduct}
          style={{ background: '#7cb305', borderColor: '#7cb305' }}
        >
          Add product
        </Button>
      </TableToolbar>
      <Table
        tableLayout='fixed'
        scroll={{
          x: 1120,
          scrollToFirstRowOnChange: true,
        }}
        dataSource={selectedProducts.map((prod, index) => ({
          index: index,
          ...prod,
        }))}
        pagination={false}
        columns={TableColumn(
          supplierProducts,
          selectedProducts,
          setSelectedProducts,
          dispatch,
          venue?._id,
          supplierId,
          hasMore,
          currentPage,
          productsLoading,
          isManualSupplierSelected,
        )}
      />
      {selectedProducts.length > 0 ? (
        <OrderTotalBar>
          <OrderTotalTitle>Order total (what you paid)</OrderTotalTitle>
          {orderVatTotal > 0 ? (
            <OrderTotalBreakdown>
              <span>Ex VAT: £{orderExVatTotal.toFixed(2)}</span>
              <span>VAT: £{orderVatTotal.toFixed(2)}</span>
            </OrderTotalBreakdown>
          ) : null}
          {orderDiscountAmount > 0 ? (
            <OrderTotalBreakdown>
              <span>Subtotal: £{orderGrossTotal.toFixed(2)}</span>
              <span>Discount: −£{orderDiscountAmount.toFixed(2)}</span>
            </OrderTotalBreakdown>
          ) : null}
          <OrderTotalAmount>£{orderPayableTotal.toFixed(2)}</OrderTotalAmount>
        </OrderTotalBar>
      ) : null}
    </div>
  )
}

export default AddInvoiceModal

const AiBuildPanel = styled.div`
  margin-bottom: 16px;
  padding: 14px 16px;
  background: #fcffe6;
  border-radius: 6px;
  border: 1px solid #eaff8f;
`

const AiBuildHeader = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-bottom: 12px;
`

const AiBuildTitle = styled.div`
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
`

const AiBuildHint = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
  margin-top: 2px;
`

const AiBuildControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: flex-start;
`

const OrderHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
  padding: 14px 16px;
  background: #fafafa;
  border-radius: 6px;
  border: 1px solid #f0f0f0;
`

const HeaderPrimary = styled.div`
  display: grid;
  grid-template-columns: ${props =>
    props.hasSage
      ? 'minmax(120px, 1fr) minmax(180px, 1.5fr) minmax(140px, 1fr)'
      : 'minmax(120px, 1fr) minmax(200px, 2fr)'};
  gap: 10px;
  align-items: center;
`

const HeaderDates = styled.div`
  display: grid;
  grid-template-columns: minmax(140px, 1fr) minmax(140px, 1fr);
  gap: 10px;
  max-width: 420px;
`

const HeaderField = styled.div`
  min-width: 0;

  .ant-input,
  .ant-calendar-picker,
  .ant-select {
    width: 100%;
  }
`

const TableToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 10px;
  min-height: 32px;

  > .ant-btn {
    flex-shrink: 0;
    margin-left: auto;
  }
`

const ToolbarHint = styled.div`
  flex: 1;
  min-width: 0;
`

const ToolbarHintText = styled.span`
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
`

const ValidationHint = styled.span`
  color: #d46b08;
  font-size: 12px;
`

const CreateSupplierOptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 2px;
`

const CreateSupplierIcon = styled.span`
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f6ffed;
  color: #7cb305;
`

const CreateSupplierText = styled.span`
  color: #7cb305;
  font-weight: 500;
`

const OrderTotalBar = styled.div`
  margin-top: 12px;
  margin-bottom: 4px;
  padding: 12px 16px;
  border-radius: 6px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  text-align: right;
`

const OrderTotalTitle = styled.div`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 4px;
`

const OrderTotalBreakdown = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.75);
  margin-bottom: 4px;
`

const OrderTotalAmount = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.88);
`

const SupplierConfirmWrap = styled.div`
  margin-top: 4px;
`

const SupplierConfirmLead = styled.p`
  margin: 0 0 12px;
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
  line-height: 1.45;
`

const SupplierConfirmRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SupplierConfirmCard = styled.div`
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid ${props => (props.accent ? '#b7eb8f' : '#f0f0f0')};
  background: ${props => (props.accent ? '#fcffe6' : '#fafafa')};
`

const SupplierConfirmLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 4px;
`

const SupplierConfirmValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  line-height: 1.35;
  word-break: break-word;
`

const SupplierConfirmHint = styled.p`
  margin: 12px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.45);
`

/* Keep confirm footer buttons on one clean row (antd 3 wraps long okText). */
const SupplierConfirmGlobalStyle = `
.manual-order-supplier-confirm .ant-confirm-btns {
  display: flex !important;
  flex-direction: row !important;
  justify-content: flex-end !important;
  align-items: center !important;
  gap: 8px;
  float: none !important;
  margin-top: 20px;
}
.manual-order-supplier-confirm .ant-confirm-btns button {
  margin: 0 !important;
  min-width: 120px;
}
`

if (
  typeof document !== 'undefined' &&
  !document.getElementById('manual-order-supplier-confirm-style')
) {
  const style = document.createElement('style')
  style.id = 'manual-order-supplier-confirm-style'
  style.textContent = SupplierConfirmGlobalStyle
  document.head.appendChild(style)
}
